import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Select } from '@/components/ui/Select';
import { Input } from '@/components/ui/Input';
import { api } from '@/lib/api';
import { toast } from 'react-hot-toast';
import {
  Users,
  RefreshCw,
  CheckCircle,
  DollarSign,
  MessageSquare,
  FileText,
  Activity,
  Phone,
} from 'lucide-react';
import { formatCurrency, formatDate, formatPercentage } from '@/lib/formatters';

interface Operador {
  id: string;
  name: string;
  email: string;
}

interface OperadorPerformanceReport {
  operador: {
    id: string;
    name: string;
    email: string;
  };
  periodo: {
    inicio: string;
    fim: string;
  };
  acesso: {
    totalAcessos: number;
    acessosBemSucedidos: number;
    acessosMalSucedidos: number;
    primeiroAcesso: string | null;
    ultimoAcesso: string | null;
    diasAtivos: number;
    tempoMedioEntreAcessos: number;
  };
  contatos: {
    totalContatos: number;
    contatosPorTipo: Record<string, number>;
    tempoMedioEntreContatos: number;
    tempoMedioEntreContatosMesmoCliente: number;
    clientesContatados: number;
    clientesUnicos: string[];
    contatosPorDia: number;
  };
  valores: {
    totalCobrado: number;
    totalNegociado: number;
    totalPago: number;
    totalPendente: number;
    quantidadePagamentos: number;
    quantidadeNegociacoes: number;
    valorMedioPorPagamento: number;
    valorMedioPorNegociacao: number;
    taxaConversao: number;
  };
  whatsapp: {
    totalMensagensEnviadas: number;
    mensagensEnviadas: number;
    mensagensEntregues: number;
    mensagensLidas: number;
    mensagensFalhadas: number;
    taxaEntrega: number;
    taxaleitura: number;
    taxaSucesso: number;
    clientesContatados: number;
    clientesUnicos: string[];
  };
  titulos: {
    totalAtribuidos: number;
    totalResolvidos: number;
    totalVencidos: number;
    totalParciais: number;
    totalAbertos: number;
    taxaResolucao: number;
    valorTotalAtribuido: number;
    valorTotalResolvido: number;
    valorMedioPorTitulo: number;
    tempoMedioResolucao: number;
  };
  produtividade: {
    contatosPorDia: number;
    mensagensPorDia: number;
    valorCobradoPorDia: number;
    titulosResolvidosPorDia: number;
    eficienciaGeral: number;
  };
}

export const OperadorPerformanceReportPage: React.FC = () => {
  const [selectedOperador, setSelectedOperador] = useState<string>('');
  const [dateFrom, setDateFrom] = useState<string>(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState<string>(() => {
    return new Date().toISOString().split('T')[0];
  });
  const [viewMode, setViewMode] = useState<'individual' | 'comparativo'>('individual');
  const [operadores, setOperadores] = useState<Operador[]>([]);

  // Carregar operadores
  useEffect(() => {
    const loadOperadores = async () => {
      try {
        const response = await api.get('/users?role=OPERADOR&isActive=true');
        setOperadores(response.data.data.users || []);
      } catch (error) {
        toast.error('Erro ao carregar operadores');
      }
    };
    loadOperadores();
  }, []);

  // Query para relatório individual
  const { data: reportData, isLoading, refetch } = useQuery<OperadorPerformanceReport | null>({
    queryKey: ['operador-performance', selectedOperador, dateFrom, dateTo],
    queryFn: async () => {
      if (!selectedOperador) return null;
      const params = new URLSearchParams();
      if (dateFrom) params.append('startDate', dateFrom);
      if (dateTo) params.append('endDate', dateTo);

      const response = await api.get(`/reports/operador-performance/${selectedOperador}?${params.toString()}`);
      return response.data.data;
    },
    enabled: !!selectedOperador && viewMode === 'individual',
  });

  // Query para relatório comparativo
  const { data: comparativoData, isLoading: isLoadingComparativo } = useQuery<{
    relatorios: OperadorPerformanceReport[];
    totalOperadores: number;
    periodo: { inicio: string; fim: string };
  }>({
    queryKey: ['operador-performance-all', dateFrom, dateTo],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (dateFrom) params.append('startDate', dateFrom);
      if (dateTo) params.append('endDate', dateTo);

      const response = await api.get(`/reports/operador-performance?${params.toString()}`);
      return response.data.data;
    },
    enabled: viewMode === 'comparativo',
  });

  const formatMinutes = (minutes: number) => {
    if (minutes < 60) {
      return `${Math.round(minutes)} min`;
    }
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return `${hours}h ${mins}min`;
  };

  const formatDays = (days: number) => {
    if (days < 1) {
      return `${Math.round(days * 24)}h`;
    }
    return `${Math.round(days)} dias`;
  };

  const getEficienciaColor = (eficiencia: number) => {
    if (eficiencia >= 80) return 'text-green-600';
    if (eficiencia >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getEficienciaBadge = (eficiencia: number): 'default' | 'secondary' | 'destructive' | 'warning' => {
    if (eficiencia >= 80) return 'default';
    if (eficiencia >= 60) return 'warning';
    return 'destructive';
  };

  if (viewMode === 'comparativo') {
    return (
      <div className="container mx-auto p-6 space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold">Relatório Comparativo de Desempenho</h1>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setViewMode('individual')}
            >
              Individual
            </Button>
            <Button
              variant="default"
              onClick={() => setViewMode('comparativo')}
            >
              Comparativo
            </Button>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Filtros</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Data Inicial</label>
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Data Final</label>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                />
              </div>
              <div className="flex items-end">
                <Button onClick={() => refetch()} className="w-full">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Atualizar
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {isLoadingComparativo ? (
          <div className="text-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4" />
            <p>Carregando relatório comparativo...</p>
          </div>
        ) : comparativoData ? (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="text-2xl font-bold">{comparativoData.totalOperadores}</div>
                  <p className="text-sm text-muted-foreground">Operadores</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-2xl font-bold">
                    {comparativoData.relatorios.reduce((sum, r) => sum + r.contatos.totalContatos, 0)}
                  </div>
                  <p className="text-sm text-muted-foreground">Total de Contatos</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-2xl font-bold">
                    {formatCurrency(
                      comparativoData.relatorios.reduce((sum, r) => sum + r.valores.totalCobrado, 0)
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">Total Cobrado</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-2xl font-bold">
                    {comparativoData.relatorios.reduce((sum, r) => sum + r.titulos.totalResolvidos, 0)}
                  </div>
                  <p className="text-sm text-muted-foreground">Títulos Resolvidos</p>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Ranking de Eficiência</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-2">Operador</th>
                        <th className="text-right p-2">Eficiência</th>
                        <th className="text-right p-2">Contatos</th>
                        <th className="text-right p-2">Valor Cobrado</th>
                        <th className="text-right p-2">Títulos Resolvidos</th>
                        <th className="text-right p-2">Taxa Resolução</th>
                      </tr>
                    </thead>
                    <tbody>
                      {comparativoData.relatorios.map((relatorio) => (
                        <tr key={relatorio.operador.id} className="border-b hover:bg-gray-50">
                          <td className="p-2 font-medium">{relatorio.operador.name}</td>
                          <td className="p-2 text-right">
                            <Badge variant={getEficienciaBadge(relatorio.produtividade.eficienciaGeral)}>
                              {relatorio.produtividade.eficienciaGeral.toFixed(1)}%
                            </Badge>
                          </td>
                          <td className="p-2 text-right">{relatorio.contatos.totalContatos}</td>
                          <td className="p-2 text-right">{formatCurrency(relatorio.valores.totalCobrado)}</td>
                          <td className="p-2 text-right">{relatorio.titulos.totalResolvidos}</td>
                          <td className="p-2 text-right">
                            {formatPercentage(relatorio.titulos.taxaResolucao)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Relatório de Desempenho do Operador</h1>
        <div className="flex gap-2">
          <Button
            variant="default"
            onClick={() => setViewMode('individual')}
          >
            Individual
          </Button>
          <Button
            variant="outline"
            onClick={() => setViewMode('comparativo')}
          >
            Comparativo
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filtros</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Operador</label>
              <Select
                value={selectedOperador}
                onValueChange={setSelectedOperador}
              >
                <option value="">Selecione um operador</option>
                {operadores.map((op) => (
                  <option key={op.id} value={op.id}>
                    {op.name}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Data Inicial</label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Data Final</label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </div>
            <div className="flex items-end">
              <Button onClick={() => refetch()} className="w-full" disabled={!selectedOperador}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Atualizar
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {!selectedOperador ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Users className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">Selecione um operador para visualizar o relatório</p>
          </CardContent>
        </Card>
      ) : isLoading ? (
        <div className="text-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4" />
          <p>Carregando relatório...</p>
        </div>
      ) : reportData ? (
        <div className="space-y-6">
          {/* Header com informações do operador */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-2xl font-bold">{reportData.operador.name}</h2>
                  <p className="text-muted-foreground">{reportData.operador.email}</p>
                  <p className="text-sm text-muted-foreground mt-2">
                    Período: {formatDate(reportData.periodo.inicio)} até {formatDate(reportData.periodo.fim)}
                  </p>
                </div>
                <div className="text-right">
                  <div className={`text-4xl font-bold ${getEficienciaColor(reportData.produtividade.eficienciaGeral)}`}>
                    {reportData.produtividade.eficienciaGeral.toFixed(1)}%
                  </div>
                  <p className="text-sm text-muted-foreground">Eficiência Geral</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Métricas de Produtividade */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Contatos/Dia</p>
                    <p className="text-2xl font-bold">{reportData.produtividade.contatosPorDia.toFixed(1)}</p>
                  </div>
                  <Phone className="w-8 h-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Mensagens/Dia</p>
                    <p className="text-2xl font-bold">{reportData.produtividade.mensagensPorDia.toFixed(1)}</p>
                  </div>
                  <MessageSquare className="w-8 h-8 text-green-500" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Valor Cobrado/Dia</p>
                    <p className="text-2xl font-bold">{formatCurrency(reportData.produtividade.valorCobradoPorDia)}</p>
                  </div>
                  <DollarSign className="w-8 h-8 text-yellow-500" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Títulos Resolvidos/Dia</p>
                    <p className="text-2xl font-bold">{reportData.produtividade.titulosResolvidosPorDia.toFixed(1)}</p>
                  </div>
                  <CheckCircle className="w-8 h-8 text-green-500" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Estatísticas de Acesso */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="w-5 h-5" />
                Estatísticas de Acesso
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Total de Acessos</p>
                  <p className="text-2xl font-bold">{reportData.acesso.totalAcessos}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Acessos Bem-Sucedidos</p>
                  <p className="text-2xl font-bold text-green-600">{reportData.acesso.acessosBemSucedidos}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Dias Ativos</p>
                  <p className="text-2xl font-bold">{reportData.acesso.diasAtivos}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Tempo Médio Entre Acessos</p>
                  <p className="text-2xl font-bold">{formatMinutes(reportData.acesso.tempoMedioEntreAcessos)}</p>
                </div>
              </div>
              {reportData.acesso.ultimoAcesso && (
                <div className="mt-4 pt-4 border-t">
                  <p className="text-sm text-muted-foreground">
                    Último acesso: {formatDate(reportData.acesso.ultimoAcesso)}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Estatísticas de Contatos */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Phone className="w-5 h-5" />
                Estatísticas de Contatos
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div>
                  <p className="text-sm text-muted-foreground">Total de Contatos</p>
                  <p className="text-2xl font-bold">{reportData.contatos.totalContatos}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Clientes Contatados</p>
                  <p className="text-2xl font-bold">{reportData.contatos.clientesContatados}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Contatos/Dia</p>
                  <p className="text-2xl font-bold">{reportData.contatos.contatosPorDia.toFixed(1)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Tempo Médio Entre Contatos</p>
                  <p className="text-2xl font-bold">{formatMinutes(reportData.contatos.tempoMedioEntreContatos)}</p>
                </div>
              </div>
              {Object.keys(reportData.contatos.contatosPorTipo).length > 0 && (
                <div className="mt-4 pt-4 border-t">
                  <p className="text-sm font-medium mb-2">Contatos por Tipo</p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(reportData.contatos.contatosPorTipo).map(([tipo, quantidade]) => (
                      <Badge key={tipo} variant="outline">
                        {tipo}: {quantidade}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Estatísticas de Valores */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="w-5 h-5" />
                Estatísticas de Valores
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div>
                  <p className="text-sm text-muted-foreground">Total Cobrado</p>
                  <p className="text-2xl font-bold text-green-600">{formatCurrency(reportData.valores.totalCobrado)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Pago</p>
                  <p className="text-2xl font-bold text-blue-600">{formatCurrency(reportData.valores.totalPago)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Pendente</p>
                  <p className="text-2xl font-bold text-yellow-600">{formatCurrency(reportData.valores.totalPendente)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Taxa de Conversão</p>
                  <p className="text-2xl font-bold">{formatPercentage(reportData.valores.taxaConversao)}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4 pt-4 border-t">
                <div>
                  <p className="text-sm text-muted-foreground">Total Negociado</p>
                  <p className="text-xl font-semibold">{formatCurrency(reportData.valores.totalNegociado)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Quantidade de Pagamentos</p>
                  <p className="text-xl font-semibold">{reportData.valores.quantidadePagamentos}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Valor Médio por Pagamento</p>
                  <p className="text-xl font-semibold">{formatCurrency(reportData.valores.valorMedioPorPagamento)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Estatísticas de WhatsApp */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5" />
                Estatísticas de WhatsApp
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div>
                  <p className="text-sm text-muted-foreground">Total Enviadas</p>
                  <p className="text-2xl font-bold">{reportData.whatsapp.totalMensagensEnviadas}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Entregues</p>
                  <p className="text-2xl font-bold text-green-600">{reportData.whatsapp.mensagensEntregues}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Lidas</p>
                  <p className="text-2xl font-bold text-blue-600">{reportData.whatsapp.mensagensLidas}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Falhadas</p>
                  <p className="text-2xl font-bold text-red-600">{reportData.whatsapp.mensagensFalhadas}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4 pt-4 border-t">
                <div>
                  <p className="text-sm text-muted-foreground">Taxa de Entrega</p>
                  <p className="text-xl font-semibold">{formatPercentage(reportData.whatsapp.taxaEntrega)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Taxa de Leitura</p>
                  <p className="text-xl font-semibold">{formatPercentage(reportData.whatsapp.taxaleitura)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Taxa de Sucesso</p>
                  <p className="text-xl font-semibold">{formatPercentage(reportData.whatsapp.taxaSucesso)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Estatísticas de Títulos */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Estatísticas de Títulos
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
                <div>
                  <p className="text-sm text-muted-foreground">Total Atribuídos</p>
                  <p className="text-2xl font-bold">{reportData.titulos.totalAtribuidos}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Resolvidos</p>
                  <p className="text-2xl font-bold text-green-600">{reportData.titulos.totalResolvidos}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Vencidos</p>
                  <p className="text-2xl font-bold text-red-600">{reportData.titulos.totalVencidos}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Parciais</p>
                  <p className="text-2xl font-bold text-yellow-600">{reportData.titulos.totalParciais}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Abertos</p>
                  <p className="text-2xl font-bold text-blue-600">{reportData.titulos.totalAbertos}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 pt-4 border-t">
                <div>
                  <p className="text-sm text-muted-foreground">Taxa de Resolução</p>
                  <p className="text-xl font-semibold">{formatPercentage(reportData.titulos.taxaResolucao)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Valor Total Atribuído</p>
                  <p className="text-xl font-semibold">{formatCurrency(reportData.titulos.valorTotalAtribuido)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Valor Total Resolvido</p>
                  <p className="text-xl font-semibold">{formatCurrency(reportData.titulos.valorTotalResolvido)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Tempo Médio de Resolução</p>
                  <p className="text-xl font-semibold">{formatDays(reportData.titulos.tempoMedioResolucao)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      ) : null}
    </div>
  );
};

